// The guest pass's ticket code, shown big once its sale is paid (spec/13 §4; spec/05 §3.4): what the guest types on a PC
// under "I have a ticket", the pass it is for and until when it works, and a QR that opens the player web's "Claim a
// guest pass" with the code filled in — so the guest can keep the time in an account. The code is read from the server
// (`GET …/guest-passes?saleId=`), read again every second while the payment is settling.

import type { GuestPass } from '@clavian/api-client';
import { useTranslation, useVenueFormatter } from '@clavian/i18n/react';
import { encodeQr, qrPath, Skeleton } from '@clavian/ui-web';
import { useQuery } from '@tanstack/react-query';
import { useMemo, type ReactNode } from 'react';

import { useApi } from '../../api/client';
import { queryKeys } from '../../api/queries';
import { Notice } from '../../ui';
import { useGuestPasses } from './queries';

import './guests.css';

/** `https://play.clavian.io`, from the brand's `domains.play` (`GET /v1/meta`); `undefined` if it cannot be read. */
function usePlayerWebOrigin(): string | undefined {
  const api = useApi();
  const meta = useQuery({ queryKey: queryKeys.meta, queryFn: () => api.getMeta(), retry: false, staleTime: 300_000 });
  const host: unknown = (meta.data as { brand?: { domains?: { play?: unknown } } } | undefined)?.brand?.domains?.play;
  if (typeof host !== 'string' || !/^[A-Za-z0-9.-]+(?::\d+)?$/.test(host)) return undefined;
  const scheme = typeof window === 'undefined' ? 'https:' : window.location.protocol;
  return `${scheme === 'http:' ? 'http:' : 'https:'}//${host}`;
}

/** The ticket codes a paid sale's guest passes carry; nothing when the sale has none. */
export function GuestTicketCodes({ organizationId, venueId, saleId }: { readonly organizationId: string; readonly venueId: string; readonly saleId: string }): ReactNode {
  const { t } = useTranslation('console');
  const guests = useGuestPasses(organizationId, venueId, { saleId });
  const origin = usePlayerWebOrigin();

  if (guests.data === undefined) {
    if (guests.isError) return <Notice tone="warn">{t('sell.pay.guestTicket.unavailable')}</Notice>;
    return <Skeleton height="8rem" />;
  }
  const passes = guests.data.guestPasses;
  if (passes.length === 0) return null;
  return (
    <section className="console-guest-tickets" aria-label={t('sell.pay.guestTicket.title', { count: passes.length })}>
      <h3 className="console-guest-tickets__title">{t('sell.pay.guestTicket.title', { count: passes.length })}</h3>
      {passes.map((pass) => (
        <GuestTicketCard key={pass.id} pass={pass} claimUrl={origin === undefined ? undefined : `${origin}/guest-pass?code=${pass.code}`} />
      ))}
    </section>
  );
}

function GuestTicketCard({ pass, claimUrl }: { readonly pass: GuestPass; readonly claimUrl: string | undefined }): ReactNode {
  const { t } = useTranslation('console');
  const format = useVenueFormatter();
  const qr = useMemo(() => {
    if (claimUrl === undefined) return null;
    try {
      return encodeQr(claimUrl);
    } catch {
      return null;
    }
  }, [claimUrl]);
  return (
    <figure className="console-guest-ticket">
      <div className="console-guest-ticket__body">
        <span className="console-subtle">{t('sell.pay.guestTicket.code', { pass: pass.passName })}</span>
        {/* Spaced for reading aloud and typing: three and three. */}
        <strong className="console-guest-ticket__code clav-figure" aria-label={pass.code.split('').join(' ')}>
          {pass.code.slice(0, 3)}
          <span className="console-guest-ticket__gap" aria-hidden="true" />
          {pass.code.slice(3)}
        </strong>
        <span>{t('sell.pay.guestTicket.hint')}</span>
        {pass.status === 'awaiting_payment' ? (
          <span className="console-subtle" role="status">
            {t('sell.pay.guestTicket.loading')}
          </span>
        ) : pass.ticketExpiresAt === null ? null : (
          <span className="console-subtle">{t('sell.pay.guestTicket.until', { time: format.dateTime(pass.ticketExpiresAt, { date: 'medium', time: 'short' }) })}</span>
        )}
      </div>
      {qr === null ? null : (
        <div className="console-guest-ticket__qr">
          {/* A QR is read black on white, whatever the theme: its own quiet zone keeps it readable on a dark surface. */}
          <svg viewBox={`-4 -4 ${qr.size + 8} ${qr.size + 8}`} shapeRendering="crispEdges" role="img" aria-label={t('sell.pay.guestTicket.scan')} focusable="false">
            <rect x="-4" y="-4" width={qr.size + 8} height={qr.size + 8} fill="#fff" />
            <path d={qrPath(qr)} fill="#000" />
          </svg>
          <figcaption className="console-subtle">{t('sell.pay.guestTicket.scan')}</figcaption>
        </div>
      )}
    </figure>
  );
}
